/**
 * Smart Path Modal Component
 * Shows bounce pages triggered by specific answer selections
 *
 * Path Types:
 * - soft: User can dismiss and continue quiz
 * - hard: User must complete action (form submit or redirect)
 *
 * CTA Actions:
 * - capture: Show form fields, submit lead
 * - redirect: Redirect to URL
 * - close: Just close modal (soft paths only)
 */

import { createLogger } from '../core/logger';
import { isLeadStorageEnabled, isTestMode } from '../core/config';
import {
  getState,
  getAnswers,
  showSmartPath,
  closeSmartPath,
  completeSmartPath,
  generateLeadId
} from '../core/state';
import { submitLead } from '../services/api';
import { trackSmartPathShown, trackSmartPathAction } from '../services/progress';
import { trackSmartPathShown as trackSmartPathPixel } from '../services/pixels';
import { getAllTrackingData, getFBP, getFBC } from '../services/tracking';
import { SmartPathPage, FieldType, Lead } from '../types';

const logger = createLogger('SmartPathModal');

let onCloseCallback: (() => void) | null = null;

/**
 * Show a smart path modal
 */
export function showSmartPathModal(
  page: SmartPathPage,
  trigger: { questionId: string; optionIndex: number },
  onClose?: () => void
): void {
  onCloseCallback = onClose || null;

  logger.info('Showing smart path', {
    id: page.id,
    type: page.type,
    action: page.ctaAction
  });

  // Update state
  showSmartPath(page.id);

  // Track to server
  trackSmartPathShown({
    pathId: page.id,
    pathType: page.type,
    triggeredBy: trigger
  });

  // Track to pixels
  trackSmartPathPixel(page.id, page.type);

  // Render modal
  renderModal(page);
}

/**
 * Close the smart path modal
 */
export function hideSmartPathModal(): void {
  const overlay = document.getElementById('smart-path-overlay');
  if (overlay) {
    overlay.classList.add('smart-path-overlay--closing');
    setTimeout(() => {
      overlay.remove();
    }, 200);
  }

  closeSmartPath();

  if (onCloseCallback) {
    onCloseCallback();
  }

  onCloseCallback = null;
}

/**
 * Render the modal
 */
function renderModal(page: SmartPathPage): void {
  // Remove existing modal if any
  document.getElementById('smart-path-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'smart-path-overlay';
  overlay.className = 'smart-path-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'smart-path-headline');

  const showForm = page.ctaAction === 'capture' && page.fields && page.fields.length > 0;
  const canClose = page.type === 'soft';

  overlay.innerHTML = `
    <div class="smart-path-modal">
      ${canClose ? `
        <button class="smart-path-close" aria-label="Close" type="button">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 2L18 18M2 18L18 2" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
          </svg>
        </button>
      ` : ''}

      <div class="smart-path-content">
        <h2 class="smart-path-headline" id="smart-path-headline">${escapeHtml(page.headline)}</h2>
        ${page.subhead ? `<p class="smart-path-subhead">${escapeHtml(page.subhead)}</p>` : ''}
        ${page.body ? `<div class="smart-path-body">${escapeHtml(page.body)}</div>` : ''}

        ${showForm ? `
          <form class="smart-path-form" id="smart-path-form">
            <div class="smart-path-fields">
              ${renderFields(page.fields!)}
            </div>

            ${page.showConsent ? `
              <label class="smart-path-consent">
                <input type="checkbox" name="consent" id="smart-path-consent" required>
                <span>${escapeHtml(page.consentText || 'I agree to receive communications')}</span>
              </label>
            ` : ''}

            <div class="smart-path-errors" id="smart-path-errors"></div>

            <button type="submit" class="btn btn-primary btn-lg smart-path-cta">
              <span class="btn-text">${escapeHtml(page.ctaText)}</span>
              <span class="btn-loading" hidden>
                <span class="loading-spinner loading-spinner--small"></span>
              </span>
            </button>
          </form>
        ` : `
          <button type="button" class="btn btn-primary btn-lg smart-path-cta" id="smart-path-action">
            ${escapeHtml(page.ctaText)}
          </button>
        `}
      </div>

      <div class="smart-path-success" id="smart-path-success" hidden>
        <div class="success-icon">✓</div>
        <h3 class="success-title">${escapeHtml(page.successTitle || 'Thank You!')}</h3>
        <p class="success-message">${escapeHtml(page.successMessage || 'Your information has been received.')}</p>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  // Animate in
  requestAnimationFrame(() => {
    overlay.classList.add('smart-path-overlay--visible');
  });

  // Attach event listeners
  attachEventListeners(page);

  // Focus first input or CTA button
  setTimeout(() => {
    const firstInput = overlay.querySelector('input:not([type="checkbox"])') as HTMLInputElement;
    const ctaButton = overlay.querySelector('.smart-path-cta') as HTMLButtonElement;
    (firstInput || ctaButton)?.focus();
  }, 100);
}

/**
 * Render form fields
 */
function renderFields(fields: FieldType[]): string {
  return fields.map(field => {
    switch (field) {
      case 'name':
        return renderField('name', 'Your Name', 'text', 'name', 'John Smith');
      case 'firstName':
        return renderField('firstName', 'First Name', 'text', 'given-name', 'John');
      case 'lastName':
        return renderField('lastName', 'Last Name', 'text', 'family-name', 'Smith');
      case 'email':
        return renderField('email', 'Email Address', 'email', 'email', 'your@email.com');
      case 'phone':
        return renderField('phone', 'Phone Number', 'tel', 'tel', '(555) 123-4567');
      default:
        return '';
    }
  }).join('');
}

/**
 * Render a single form field
 */
function renderField(
  name: string,
  label: string,
  type: string,
  autocomplete: string,
  placeholder: string
): string {
  return `
    <div class="form-field">
      <label for="sp-${name}" class="form-label">${label}</label>
      <input
        type="${type}"
        id="sp-${name}"
        name="${name}"
        class="form-input"
        placeholder="${placeholder}"
        autocomplete="${autocomplete}"
        required
      >
    </div>
  `;
}

/**
 * Attach event listeners
 */
function attachEventListeners(page: SmartPathPage): void {
  const overlay = document.getElementById('smart-path-overlay');
  if (!overlay) return;

  // Close button (soft paths only)
  const closeBtn = overlay.querySelector('.smart-path-close');
  closeBtn?.addEventListener('click', () => {
    trackSmartPathAction({ pathId: page.id, action: 'close' });
    hideSmartPathModal();
  });

  // Click outside to close (soft paths only)
  if (page.type === 'soft') {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        trackSmartPathAction({ pathId: page.id, action: 'close' });
        hideSmartPathModal();
      }
    });

    // Escape key to close
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        trackSmartPathAction({ pathId: page.id, action: 'close' });
        hideSmartPathModal();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
  }

  // Form submission
  const form = document.getElementById('smart-path-form') as HTMLFormElement;
  if (form) {
    form.addEventListener('submit', (e) => handleFormSubmit(e, page));
  }

  // Non-form CTA button (redirect or close action)
  const actionBtn = document.getElementById('smart-path-action');
  if (actionBtn) {
    actionBtn.addEventListener('click', () => handleCtaAction(page));
  }
}

/**
 * Handle CTA action (non-form)
 */
function handleCtaAction(page: SmartPathPage): void {
  if (page.ctaAction === 'redirect' && page.redirectUrl) {
    trackSmartPathAction({ pathId: page.id, action: 'redirect' });
    completeSmartPath(page.id);
    window.location.href = page.redirectUrl;
  } else if (page.ctaAction === 'close') {
    trackSmartPathAction({ pathId: page.id, action: 'close' });
    hideSmartPathModal();
  }
}

/**
 * Handle form submission
 */
async function handleFormSubmit(e: Event, page: SmartPathPage): Promise<void> {
  e.preventDefault();

  const form = e.target as HTMLFormElement;
  const submitBtn = form.querySelector('.smart-path-cta') as HTMLButtonElement;
  const errors: string[] = [];

  // Get field values
  const name = getFieldValue('sp-name');
  const firstName = getFieldValue('sp-firstName');
  const lastName = getFieldValue('sp-lastName');
  const email = getFieldValue('sp-email');
  const phone = getFieldValue('sp-phone');
  const consent = (document.getElementById('smart-path-consent') as HTMLInputElement)?.checked ?? true;

  // Validate
  if (page.fields?.includes('email') && email && !isValidEmail(email)) {
    errors.push('Please enter a valid email address');
  }

  if (page.showConsent && !consent) {
    errors.push('Please check the consent box to continue');
  }

  if (errors.length > 0) {
    showErrors(errors);
    return;
  }

  // Show loading
  submitBtn.disabled = true;
  submitBtn.querySelector('.btn-text')?.setAttribute('hidden', '');
  submitBtn.querySelector('.btn-loading')?.removeAttribute('hidden');

  // Build lead data
  const state = getState();
  const leadId = generateLeadId();
  const trackingData = getAllTrackingData();

  const leadData: Lead = {
    leadID: leadId,
    funnelID: state.funnel?.funnelID || '',
    funnelVersion: state.funnel?.version || 0,
    timestamp: new Date().toISOString(),
    testMode: isTestMode(),

    name: name || null,
    firstName: firstName || null,
    lastName: lastName || null,
    email: email || null,
    phone: phone || null,

    contactConsent: consent,
    consentText: page.consentText || null,

    answers: getAnswers(),

    utmParams: trackingData.utmParams,
    customParams: {
      ...trackingData.customParams,
      smartPathId: page.id,
      smartPathName: page.name
    },
    referrerInfo: trackingData.referrerInfo,

    fbc: getFBC(),
    fbp: getFBP(),

    gdprConsent: state.compliance.gdprConsent,

    userAgent: navigator.userAgent,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };

  // Submit if storage is enabled
  if (isLeadStorageEnabled()) {
    const result = await submitLead(leadData);

    if (!result.success) {
      logger.error('Smart path lead submission failed', result.error);
      showErrors(['Failed to submit. Please try again.']);
      submitBtn.disabled = false;
      submitBtn.querySelector('.btn-text')?.removeAttribute('hidden');
      submitBtn.querySelector('.btn-loading')?.setAttribute('hidden', '');
      return;
    }
  }

  // Track action
  trackSmartPathAction({ pathId: page.id, action: 'submit' });
  completeSmartPath(page.id);

  logger.info('Smart path form submitted', { pathId: page.id, leadId });

  // Show success
  showSuccess(page);
}

/**
 * Show success state
 */
function showSuccess(page: SmartPathPage): void {
  const content = document.querySelector('.smart-path-content');
  const success = document.getElementById('smart-path-success');

  if (content && success) {
    content.setAttribute('hidden', '');
    success.removeAttribute('hidden');
  }

  // Auto-close or redirect after delay
  setTimeout(() => {
    if (page.redirectUrl) {
      window.location.href = page.redirectUrl;
    } else {
      hideSmartPathModal();
    }
  }, 2000);
}

/**
 * Show validation errors
 */
function showErrors(errors: string[]): void {
  const container = document.getElementById('smart-path-errors');
  if (container) {
    container.innerHTML = errors.map(e => `<p class="form-error">${escapeHtml(e)}</p>`).join('');
  }
}

/**
 * Get field value
 */
function getFieldValue(id: string): string {
  const input = document.getElementById(id) as HTMLInputElement;
  return input?.value.trim() || '';
}

/**
 * Validate email
 */
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Escape HTML
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Check if a smart path modal is currently open
 */
export function isSmartPathOpen(): boolean {
  return document.getElementById('smart-path-overlay') !== null;
}
